import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/primitives'
import { formatUsd } from '@/lib/money'
import { t } from '@/i18n'

/**
 * Adds something the shop does not stock: a repair, a delivery, a one-off.
 *
 * The cost is asked for and not optional. Left blank it would default to nothing and the line
 * would read as pure profit, quietly overstating the day's margin by the whole amount — so the
 * field is there, and a shop with genuinely no cost enters zero and means it.
 */
export function CustomItemDialog({
  open,
  onClose,
  onAdd,
}: {
  open: boolean
  onClose: () => void
  onAdd: (name: string, priceUsd: number, costUsd: number, quantity: number) => void
}) {
  const [name, setName] = useState('')
  const [price, setPrice] = useState('')
  const [cost, setCost] = useState('')
  const [quantity, setQuantity] = useState('1')

  useEffect(() => {
    if (open) {
      setName('')
      setPrice('')
      setCost('')
      setQuantity('1')
    }
  }, [open])

  const priceValue = Number(price || 0)
  const costValue = Number(cost || 0)
  const quantityValue = Number(quantity || 0)
  const ready = name.trim() !== '' && priceValue > 0 && quantityValue > 0 && cost !== ''

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('Custom item')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            disabled={!ready}
            onClick={() => {
              onAdd(name, priceValue, costValue, quantityValue)
              onClose()
            }}
          >
            {t('Add to cart')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label={t('What is it')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('Door repair')}
          hint={t('Printed on the receipt exactly as typed')}
          autoFocus
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            label={t('Price each')}
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
            className="tabular"
            placeholder="0.00"
          />
          <Input
            label={t('Cost each')}
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={cost}
            onChange={(event) => setCost(event.target.value)}
            className="tabular"
            placeholder="0.00"
            hint={t('What it cost you')}
          />
        </div>
        <Input
          label={t('Quantity')}
          type="number"
          inputMode="numeric"
          min="1"
          value={quantity}
          onChange={(event) => setQuantity(event.target.value)}
          className="tabular"
        />

        {ready && (
          <p className="surface-sunken rounded-lg px-3 py-2 text-sm">
            <span className="text-[var(--text-secondary)]">{t('Adds')}</span>
            <b className="tabular">{formatUsd(priceValue * quantityValue)}</b>
            <span className="text-[var(--text-secondary)]">
              {' '}
              to the cart, at {formatUsd(costValue * quantityValue)} of cost.
            </span>
          </p>
        )}
      </div>
    </Modal>
  )
}

/**
 * Takes an amount off one line or off the whole order.
 *
 * Entered in money rather than as a percentage: a shopkeeper knocking something off says "call it
 * five dollars", and a percentage would land on an amount neither of them chose.
 */
export function DiscountDialog({
  open,
  title,
  maximum,
  current,
  onClose,
  onApply,
}: {
  open: boolean
  title: string
  /** What the discount is being taken off, so it cannot exceed it. */
  maximum: number
  current: number
  onClose: () => void
  onApply: (amount: number) => void
}) {
  const [amount, setAmount] = useState('')

  useEffect(() => {
    if (open) setAmount(current > 0 ? String(current) : '')
  }, [open, current])

  const value = Number(amount || 0)
  // A discount larger than the thing being discounted would hand money back at the till.
  const tooMuch = value > maximum

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('Cancel')}
          </Button>
          <Button
            disabled={value < 0 || tooMuch}
            onClick={() => {
              onApply(value)
              onClose()
            }}
          >
            {value > 0 ? t('Take off {amount}', { amount: formatUsd(value) }) : t('Remove discount')}
          </Button>
        </>
      }
    >
      <Input
        label={t('Discount')}
        type="number"
        inputMode="decimal"
        step="0.01"
        min="0"
        value={amount}
        onChange={(event) => setAmount(event.target.value)}
        className="tabular text-lg"
        placeholder="0.00"
        hint={`Up to ${formatUsd(maximum)}. Leave at zero to remove it.`}
        error={tooMuch ? `That is more than the ${formatUsd(maximum)} being discounted.` : undefined}
        autoFocus
      />
    </Modal>
  )
}
